import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemsGqlQuery
} from '../../../../types';
import {
  FetchCacheItemTypesFilters,
  FetchItemTypesCacheKey,
  FetchItemTypesGqlQuery,
  FetchItemTypesLimit,
  FetchItemTypesPage,
  FetchItemTypesSort
} from '../../itemTypesTypes';
import {
  FETCH_CACHE_ITEM_TYPES_QUERY,
  FetchCacheItemTypesQueryResponse
} from '../../queries/fetchCacheItemTypes.query';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_ITEM_TYPES_FILTERS,
  INITIAL_ITEM_TYPES_LIMIT,
  INITIAL_ITEM_TYPES_PAGE,
  INITIAL_ITEM_TYPES_SORT,
  ItemTypesPermissions
} from '../../itemTypesConstants';

interface ItemTypesWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
}

interface ItemTypesWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

interface ItemTypesDefaultOptions {
  action?: string;
  cacheKey: FetchItemTypesCacheKey;
  query?: FetchItemTypesGqlQuery;
  initialFilters?: FetchCacheItemTypesFilters;
  initialSort?: FetchItemTypesSort;
  initialPage?: FetchItemTypesPage;
  initialLimit?: FetchItemTypesLimit;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
}

type ItemTypesOptions = ItemTypesDefaultOptions &
  (ItemTypesWithPrefetchItemOptions | ItemTypesWithoutPrefetchItemOptions);

const scope = 'itemTypes';

function useCachePaginatedItemTypes<
  FetchItemTypesType = FetchCacheItemTypesQueryResponse
>({
  action = ItemTypesPermissions.READ_ITEM_TYPES_INDEX_PAGE,
  cacheKey,
  initialFilters = INITIAL_ITEM_TYPES_FILTERS,
  initialSort = INITIAL_ITEM_TYPES_SORT,
  initialPage = INITIAL_ITEM_TYPES_PAGE,
  initialLimit = INITIAL_ITEM_TYPES_LIMIT,
  fetchItemCacheKey,
  fetchItemQuery,
  query = FETCH_CACHE_ITEM_TYPES_QUERY,
  source = FetchCacheItemsSources.DB,
  trackTotalHits,
  options = {}
}: ItemTypesOptions) {
  const {
    data,
    items,
    itemsError,
    itemsErrorMessage,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    updateItemCache,
    updateItemsCache,
    clearItemsFilters,
    filterItems,
    changeItemsFilters,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    prefetchItem
  } = useCacheIndexQuery<FetchItemTypesType>({
    action,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    scope,
    query,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    fetchItemCacheKey,
    fetchItemQuery,
    options,
    source,
    trackTotalHits
  });

  return {
    itemTypesData: data,
    itemTypes: items,
    itemTypesError: itemsError,
    itemTypesErrorMessage: itemsErrorMessage,
    itemTypesTotalCount: itemsTotalCount,
    itemTypesFetched: isFetched,
    itemTypesLoading: isLoading,
    itemTypesIsPlaceholderData: isPlaceholderData,
    itemTypesFilters: currentFilters,
    itemTypesSort: currentSort,
    itemTypesPage: currentPage,
    itemTypesLimit: currentLimit,
    updateItemTypeCache: updateItemCache,
    updateItemTypesCache: updateItemsCache,
    filterItemTypes: filterItems,
    changeItemTypesFilters: changeItemsFilters,
    clearItemTypesFilters: clearItemsFilters,
    sortItemTypes: sortItems,
    paginateItemTypes: paginateItems,
    limitItemTypes: limitItems,
    prefetchItemTypes: prefetchItems,
    prefetchItemType: prefetchItem
  };
}

export default useCachePaginatedItemTypes;
